import * as React from "react";
import { useRef, useState } from "react";
import { Form, Table } from "react-bootstrap";
import style from "./style.module.css";

interface Column<T> {
  dataField: keyof T;
  label?: string;
  placeholder?: string;
  options?: {
    label: string;
    value: string;
  }[];
}

type SearchTexts<T> = {
  [key in keyof T]: string;
};

interface BootstrapTableProps<T> {
  data: T[];
  columns: Column<T>[];
  idField: keyof T;
  selectedIds: string[];
  onRowSelect?: (row: T, isSelected: boolean) => void;
  onSelectAll?: (rows: T[], isSelected: boolean) => void;
}

export function BootstrapTable<T>(props: BootstrapTableProps<T>) {
  const { data, columns, idField, selectedIds, onRowSelect, onSelectAll } =
    props;

  const [searchTexts, setSearchTexts] = useState<SearchTexts<T>>(
    Object.fromEntries(columns.map((_) => [_.dataField, ""])) as SearchTexts<T>,
  );
  const selectedAllRef = useRef<HTMLInputElement>(null);

  const actualData = data.filter((row) => {
    return columns.every((column) => {
      const value = row[column.dataField];
      const searchText = searchTexts[column.dataField];
      if (searchText) {
        return String(value).toLowerCase().includes(searchText.toLowerCase());
      }
      return true;
    });
  });

  const allSelected = actualData.every((row) =>
    selectedIds.includes(row[idField] as string),
  );

  if (selectedAllRef.current) {
    selectedAllRef.current.indeterminate =
      !allSelected && selectedIds.length > 0;
  }

  const handleSelectRow = (row: T) => {
    onRowSelect?.(row, !selectedIds.includes(row[idField] as string));
  };

  const handleSelectAll = () => {
    onSelectAll?.(actualData, !selectedIds.length);
  };

  return (
    <div className={style.tableContainer}>
      <Table striped bordered hover className={style.table}>
        <thead>
          <tr>
            <td style={{ verticalAlign: "middle" }}>
              <Form.Check
                checked={allSelected}
                ref={selectedAllRef}
                onChange={handleSelectAll}
              />
            </td>
            {columns.map((column, index) => (
              <td key={index}>
                <div className="fw-bold">{column.label}</div>
                {column.options ? (
                  <Form.Select
                    value={searchTexts[column.dataField]}
                    onChange={(e) => {
                      const newValue = e.currentTarget.value;
                      setSearchTexts((prev) => ({
                        ...prev,
                        [column.dataField]: newValue,
                      }));
                    }}
                  >
                    <option value="">{column.placeholder}</option>
                    {column.options.map((option, index) => (
                      <option key={index} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                ) : (
                  <Form.Control
                    placeholder={column.placeholder}
                    value={searchTexts[column.dataField]}
                    onChange={(e) => {
                      const newValue = e.currentTarget.value;
                      setSearchTexts((prev) => ({
                        ...prev,
                        [column.dataField]: newValue,
                      }));
                    }}
                  />
                )}
              </td>
            ))}
          </tr>
        </thead>
        <tbody>
          {actualData.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              onClick={(e) => {
                e.stopPropagation();
                handleSelectRow(row);
              }}
            >
              <td>
                <Form.Check
                  checked={selectedIds.includes(row[idField] as string)}
                  onChange={() => handleSelectRow(row)}
                />
              </td>
              {columns.map((column, colIndex) => (
                <td key={colIndex}>{row[column.dataField] as string}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}
